// import React, { useRef, useEffect, useState, useCallback } from 'react';
// import {
//   Box,
//   InputGroup,
//   InputLeftElement,
//   Input,
//   Spinner,
//   FormControl,
//   FormLabel,
//   Select,
//   Text,
//   Button,
//   HStack,
//   VStack,
//   useColorModeValue,
//   Icon,
// } from '@chakra-ui/react';
// import { SearchIcon, AddIcon } from '@chakra-ui/icons';
// import { Lead } from '../../../utils/types';
// import { fetchLeadsWithUrl } from '../../../hooks/leads';
// import baseUrl from '../../../config/baseURL';

// interface LeadSelectorProps {
//   orgId: string;
//   selectedLead?: string;
//   onSelect: (leadId: string) => void;
//   onAddNew?: () => void;
// }

// export const LeadSelector: React.FC<LeadSelectorProps> = ({
//   orgId,
//   selectedLead,
//   onSelect,
//   onAddNew,
// }) => {
//   const [leads, setLeads] = useState<Lead[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [nextUrl, setNextUrl] = useState<string | null>(null);
//   const [searchTerm, setSearchTerm] = useState('');

//   const listRef = useRef<HTMLDivElement | null>(null);
//   const bg = useColorModeValue('gray.50', 'gray.700');

//   // ✅ Main data fetcher
//   const fetchLeads = useCallback(
//     async (url?: string | null, append = false) => {
//       setLoading(true);
//       try {
//         const response = await fetchLeadsWithUrl(orgId, url, {
//           search: searchTerm,
//         });
//         if (response.success) {
//           setLeads((prev) =>
//             append ? [...prev, ...response.data] : response.data,
//           );
//           setNextUrl(response.data?.next || null);
//         }
//       } catch (error) {
//         console.error('Error fetching leads:', error);
//       } finally {
//         setLoading(false);
//       }
//     },
//     [orgId, searchTerm],
//   );

//   // ✅ Initial + search reload
//   useEffect(() => {
//     const timeout = setTimeout(() => {
//       fetchLeads(null, false);
//     }, 400); // debounce search
//     return () => clearTimeout(timeout);
//   }, [fetchLeads, searchTerm]);

//   // ✅ Infinite scroll observer
//   useEffect(() => {
//     const sentinel = listRef.current;
//     if (!sentinel || !nextUrl) return;

//     const observer = new IntersectionObserver(
//       (entries) => {
//         if (entries[0].isIntersecting && !loading && nextUrl) {
//           fetchLeads(nextUrl, true);
//         }
//       },
//       { rootMargin: '150px' },
//     );

//     observer.observe(sentinel);
//     return () => observer.disconnect();
//   }, [nextUrl, loading, fetchLeads]);

//   return (
//     <FormControl>
//       <FormLabel fontWeight="medium">Select Lead</FormLabel>
//       <VStack align="stretch" spacing={3}>
//         {/* 🔍 Search Input */}
//         <InputGroup>
//           <InputLeftElement pointerEvents="none">
//             <SearchIcon color="gray.400" />
//           </InputLeftElement>
//           <Input
//             placeholder="Search leads by name, email, or phone..."
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             bg={bg}
//           />
//         </InputGroup>

//         {/* 🧾 Lead Select Dropdown */}
//         <Select
//           placeholder="Select a lead"
//           value={selectedLead || ''}
//           onChange={(e) => onSelect(e.target.value)}
//           bg={bg}
//           size="md"
//         >
//           {leads &&
//             leads.length > 0 &&
//             leads?.map((lead) => (
//               <option key={lead.id} value={lead.id}>
//                 {lead.first_name} {lead.second_name || ''} —{' '}
//                 {lead.email || lead.phone || 'No contact info'}
//               </option>
//             ))}
//         </Select>

//         {/* 🔄 Infinite scroll + Loading */}
//         {loading && (
//           <HStack justify="center" py={2}>
//             <Spinner size="sm" />
//             <Text fontSize="sm" color="gray.500">
//               Loading leads...
//             </Text>
//           </HStack>
//         )}
//         {!loading && nextUrl && <Box ref={listRef} h="1px" />}

//         {/* ➕ Add New Lead */}
//         <Button
//           leftIcon={<Icon as={AddIcon} />}
//           colorScheme="green"
//           variant="outline"
//           w="full"
//           onClick={onAddNew}
//         >
//           Add New Lead
//         </Button>
//       </VStack>
//     </FormControl>
//   );
// };

import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  InputGroup,
  InputLeftElement,
  Input,
  Spinner,
  FormControl,
  FormLabel,
  Select,
  Text,
  Button,
  HStack,
  VStack,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon } from '@chakra-ui/icons';
import { Lead } from '../../../utils/types';
import { fetchLeadsWithUrl } from '../../../hooks/leads';

interface LeadSelectorProps {
  orgId: string;
  selectedLead?: string;
  onSelect: (leadId: string) => void;
  onAddNew?: () => void;
}

export const LeadSelector: React.FC<LeadSelectorProps> = ({
  orgId,
  selectedLead,
  onSelect,
  onAddNew,
}) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const bg = useColorModeValue('gray.50', 'gray.700');

  // ------------------------------------------------
  // 🔥 FETCH LEADS (Handles pagination + searching)
  // ------------------------------------------------
  const loadLeads = useCallback(
    async (url: string | null, append: boolean = false) => {
      setLoading(true);
      try {
        const response = await fetchLeadsWithUrl(orgId, url, {
          search: searchTerm,
        });

        if (!response.success) return;

        const results = response.data.results ?? response.data;
        const next = response.data.next ?? null;

        setLeads((prev) => (append ? [...prev, ...results] : results));
        setNextUrl(next);
      } catch (err) {
        console.error('Lead fetch error:', err);
      }
      setLoading(false);
    },
    [orgId, searchTerm],
  );

  // ------------------------------------------------
  // 🔍 Search debounce & reload
  // ------------------------------------------------
  useEffect(() => {
    const timeout = setTimeout(() => {
      loadLeads(null, false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchTerm, loadLeads]);

  // ------------------------------------------------
  // 🔄 Infinite scroll: Load next page when sentinel appears
  // ------------------------------------------------
  useEffect(() => {
    if (!nextUrl) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) {
          loadLeads(nextUrl, true);
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [nextUrl, loading, loadLeads]);

  // ------------------------------------------------
  // UI
  // ------------------------------------------------
  return (
    <FormControl>
      <FormLabel fontWeight="medium">Select Lead</FormLabel>

      <VStack align="stretch" spacing={3}>
        {/* 🔍 Search Bar */}
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>

          <Input
            placeholder="Search leads by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg={bg}
          />
        </InputGroup>

        {/* 🧾 Leads Dropdown */}
        <Select
          placeholder="Select a lead"
          value={selectedLead || ''}
          onChange={(e) => onSelect(e.target.value)}
          bg={bg}
        >
          {leads.map((lead) => (
            <option key={lead.id} value={lead.id}>
              {lead.first_name} {lead.second_name || ''} -{' '}
              {lead.email || lead.phone || 'No contact info'}
            </option>
          ))}
        </Select>

        {/* 🔄 Infinite Scroll Indicator */}
        {loading && (
          <HStack justify="center" py={2}>
            <Spinner size="sm" />
            <Text fontSize="sm">Loading leads...</Text>
          </HStack>
        )}

        {/* 🔻 Sentinel for infinite scroll */}
        {!loading && nextUrl && <Box ref={sentinelRef} height="1px" />}

        {/* ➕ Add New Lead */}
        <Button
          leftIcon={<Icon as={AddIcon} />}
          colorScheme="green"
          variant="outline"
          w="full"
          onClick={onAddNew}
        >
          Add New Lead
        </Button>
      </VStack>
    </FormControl>
  );
};
